/**
 * Pick an ontology term by label (spec §8.9.2, §8.11): the existing term search,
 * narrowed by namespace or type. Free text is kept as a label with a warning, as in the link
 * dialog (§8.11); the author never types an IRI for a term that exists.
 */
import { useState } from "react";
import { useTermSearch } from "../api/hooks";
import { localName } from "../graph/nodeInfo";
import { useT } from "../i18n";

export interface PickedTerm {
  iri: string | null;
  label: string;
}

export function TermPicker({
  value,
  onPick,
  ns,
  types,
  kind,
  placeholder,
  allowFree = false,
  readOnly,
  testId,
  extra = [],
}: {
  value: PickedTerm;
  onPick(term: PickedTerm): void;
  ns?: string;
  types?: string[];
  /** [M31] only classes, properties or individuals */
  kind?: "class" | "property" | "individual";
  placeholder?: string;
  allowFree?: boolean;
  readOnly: boolean;
  testId?: string;
  /** terms offered in addition to the search results (e.g. km: kinds QUDT does not cover) */
  extra?: Array<{ iri: string; label: string }>;
}) {
  const t = useT();
  const [q, setQ] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const search = useTermSearch(q ?? "", open, { ns, types, kind, limit: ns ? 100 : 20 });
  const needle = (q ?? "").trim().toLowerCase();
  const hits = [...extra.filter((term) => !needle || term.label.toLowerCase().includes(needle)), ...(search.data ?? [])];
  const shown = q ?? (value.label || (value.iri ? localName(value.iri) : ""));
  const free = allowFree && !value.iri && value.label.trim().length > 0;
  return (
    <span className="relative inline-flex min-w-0 flex-col">
      <input
        className="w-full min-w-0 kss-control kss-field bg-transparent px-2 py-1 text-xs"
        value={shown}
        placeholder={placeholder}
        readOnly={readOnly}
        data-testid={testId}
        title={value.iri ?? undefined}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          // keep typed text as a free label; a picked term is committed on mousedown below
          window.setTimeout(() => setOpen(false), 150);
          if (q !== null && allowFree && q !== value.label) onPick({ iri: null, label: q });
          setQ(null);
        }}
      />
      {free && <span className="text-[10px] text-amber-700" data-testid={testId ? `${testId}-free` : undefined}>{t("termPicker.free")}</span>}
      {open && !readOnly && hits.length > 0 && (
        <ul className="absolute top-full z-30 mt-0.5 max-h-56 w-72 overflow-auto rounded-md border border-neutral-200 bg-[var(--surface)] text-xs shadow-[var(--shadow-md)] dark:border-neutral-700" data-testid={testId ? `${testId}-results` : undefined}>
          {hits.map((term) => (
            <li key={term.iri}>
              <button
                className="w-full px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                title={term.iri}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick({ iri: term.iri, label: term.label });
                  setQ(null);
                  setOpen(false);
                }}
              >
                {term.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
